package twist.mena.sports.admin.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import twist.mena.sports.admin.api.AdminApiClient
import twist.mena.sports.admin.components.*
import twist.mena.sports.admin.state.AuthState
import twist.mena.sports.admin.state.UiState
import twist.mena.sports.admin.theme.*
import twist.mena.sports.shared.dto.admin.AdminEntitySearchResult
import twist.mena.sports.shared.dto.admin.AutoMatchResponse
import twist.mena.sports.shared.dto.admin.CreateEntityMappingRequest
import twist.mena.sports.shared.dto.admin.EntityMappingResponse
import twist.mena.sports.shared.dto.admin.UnmatchedEntityResponse

@Composable
fun EntityMappingsScreen(apiClient: AdminApiClient) {
    val colors = AppTheme.colors
    var activeTab by remember { mutableStateOf("mapped") }
    var entityTypeFilter by remember { mutableStateOf("team") }
    var mappingsState by remember { mutableStateOf<UiState<List<EntityMappingResponse>>>(UiState.Loading) }
    var unmatchedState by remember { mutableStateOf<UiState<List<UnmatchedEntityResponse>>>(UiState.Loading) }
    var autoMatchResult by remember { mutableStateOf<AutoMatchResponse?>(null) }
    var linkingEntity by remember { mutableStateOf<UnmatchedEntityResponse?>(null) }
    var isAutoMatching by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val isEditor = AuthState.getRole()?.let {
        it in listOf("scora_editor", "scora_admin", "scora_super_admin")
    } ?: false

    fun loadMappings() {
        scope.launch {
            mappingsState = UiState.Loading
            try {
                val mappings = apiClient.getEntityMappings(entityTypeFilter)
                mappingsState = UiState.Success(mappings)
            } catch (e: Exception) {
                mappingsState = UiState.Error("Failed to load mappings: ${e.message}")
            }
        }
    }

    fun loadUnmatched() {
        scope.launch {
            unmatchedState = UiState.Loading
            try {
                val unmatched = apiClient.getUnmatchedEntities(entityTypeFilter)
                unmatchedState = UiState.Success(unmatched)
            } catch (e: Exception) {
                unmatchedState = UiState.Error("Failed to load unmatched: ${e.message}")
            }
        }
    }

    fun loadData() {
        if (activeTab == "mapped") loadMappings() else loadUnmatched()
    }

    LaunchedEffect(activeTab, entityTypeFilter) { loadData() }

    // Tabs
    Div({ style { display(DisplayStyle.Flex); gap(Spacing.md); marginBottom(Spacing.lg); alignItems(AlignItems.Center) } }) {
        // Tab buttons
        listOf("mapped" to "Mapped", "unmatched" to "Unmatched").forEach { (key, label) ->
            Button({
                onClick { activeTab = key; autoMatchResult = null }
                style {
                    padding(Spacing.sm, Spacing.lg)
                    borderRadius(BorderRadius.md)
                    cursor("pointer")
                    fontSize(Typography.base)
                    fontWeight("600")
                    border(0.px)
                    if (activeTab == key) {
                        backgroundColor(Color(colors.primary))
                        color(Color("#ffffff"))
                    } else {
                        backgroundColor(Color(colors.cardBackground))
                        color(Color(colors.text))
                        property("border", "1px solid ${colors.border}")
                    }
                }
            }) { Text(label) }
        }

        // Entity type filter
        Div({ style { marginLeft(Spacing.lg) } }) {
            Select({
                onChange { entityTypeFilter = it.value!!; autoMatchResult = null }
                style {
                    padding(Spacing.sm, Spacing.md)
                    borderRadius(BorderRadius.md)
                    backgroundColor(Color(colors.cardBackground))
                    color(Color(colors.text))
                    fontSize(Typography.base)
                    property("border", "1px solid ${colors.border}")
                    cursor("pointer")
                }
            }) {
                listOf("team" to "Teams", "league" to "Leagues", "player" to "Players").forEach { (value, label) ->
                    Option(value, { if (entityTypeFilter == value) attr("selected", "") }) { Text(label) }
                }
            }
        }

        // Auto-Match button (unmatched tab only)
        if (activeTab == "unmatched" && isEditor) {
            Button({
                onClick {
                    scope.launch {
                        isAutoMatching = true
                        autoMatchResult = null
                        try {
                            autoMatchResult = apiClient.autoMatchEntities(entityTypeFilter)
                            loadUnmatched()
                            loadMappings()
                        } catch (e: Exception) {
                            autoMatchResult = AutoMatchResponse(0, 0, 0, listOf("Error: ${e.message}"))
                        }
                        isAutoMatching = false
                    }
                }
                style {
                    marginLeft(Spacing.md)
                    padding(Spacing.sm, Spacing.lg)
                    primaryButtonStyle(colors)
                    fontSize(Typography.sm)
                    borderRadius(BorderRadius.md)
                    if (isAutoMatching) property("opacity", "0.6")
                }
            }) { Text(if (isAutoMatching) "Matching..." else "Auto-Match") }
        }
    }

    // Auto-match result banner
    autoMatchResult?.let { result ->
        Div({
            style {
                padding(Spacing.md, Spacing.lg)
                marginBottom(Spacing.lg)
                borderRadius(BorderRadius.md)
                backgroundColor(Color(if (result.matched > 0) "#10b98115" else "#f59e0b15"))
                property("border", "1px solid ${if (result.matched > 0) colors.success else colors.warning}")
                fontSize(Typography.sm)
                color(Color(colors.text))
            }
        }) {
            P({ style { fontWeight("600"); marginBottom(Spacing.xs) } }) {
                Text("Auto-Match Results: ${result.matched} matched, ${result.alreadyMapped} already mapped, ${result.unmatched} unmatched")
            }
            if (result.details.isNotEmpty()) {
                result.details.take(10).forEach { detail ->
                    P({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) { Text(detail) }
                }
                if (result.details.size > 10) {
                    P({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) {
                        Text("... and ${result.details.size - 10} more")
                    }
                }
            }
        }
    }

    // Content
    if (activeTab == "mapped") {
        MappedTab(mappingsState, isEditor, scope, apiClient) { loadData() }
    } else {
        UnmatchedTab(unmatchedState, isEditor, onLink = { linkingEntity = it })
    }

    // Link dialog
    linkingEntity?.let { entity ->
        LinkEntityDialog(
            entity = entity,
            entityTypeFilter = entityTypeFilter,
            apiClient = apiClient,
            onLinked = {
                linkingEntity = null
                loadUnmatched()
                loadMappings()
            },
            onClose = { linkingEntity = null }
        )
    }
}

@Composable
private fun MappedTab(
    state: UiState<List<EntityMappingResponse>>,
    isEditor: Boolean,
    scope: kotlinx.coroutines.CoroutineScope,
    apiClient: AdminApiClient,
    onRefresh: () -> Unit
) {
    val colors = AppTheme.colors

    when (state) {
        is UiState.Loading -> P({ style { color(Color(colors.textMuted)) } }) { Text("Loading...") }
        is UiState.Error -> P({ style { color(Color(colors.danger)) } }) { Text(state.message) }
        is UiState.Success -> {
            val columns = listOf(
                Column<EntityMappingResponse>("Type") { m ->
                    Span({
                        style {
                            padding(2.px, Spacing.sm)
                            borderRadius(BorderRadius.sm)
                            fontSize(Typography.xs)
                            fontWeight("600")
                            backgroundColor(Color(when (m.entityType) {
                                "team" -> "#3b82f620"
                                "league" -> "#10b98120"
                                else -> "#8b5cf620"
                            }))
                            color(Color(when (m.entityType) {
                                "team" -> "#3b82f6"
                                "league" -> "#10b981"
                                else -> "#8b5cf6"
                            }))
                        }
                    }) { Text(m.entityType) }
                },
                Column("Legacy ID") { m ->
                    Span({ style { fontFamily("monospace"); fontSize(Typography.sm) } }) { Text(m.legacyEntityId) }
                },
                Column("Catalog ID") { m ->
                    Span({ style { fontFamily("monospace"); fontSize(Typography.xs); color(Color(colors.textMuted)) } }) {
                        Text(m.catalogEntityId.take(8) + "...")
                    }
                },
                Column("Confidence") { m ->
                    Span({
                        style {
                            padding(2.px, Spacing.sm)
                            borderRadius(BorderRadius.sm)
                            fontSize(Typography.xs)
                            backgroundColor(Color(when (m.matchConfidence) {
                                "HIGH" -> "#10b98120"
                                "MEDIUM" -> "#f59e0b20"
                                else -> "#ef444420"
                            }))
                            color(Color(when (m.matchConfidence) {
                                "HIGH" -> "#10b981"
                                "MEDIUM" -> "#f59e0b"
                                else -> "#ef4444"
                            }))
                        }
                    }) { Text(m.matchConfidence) }
                },
                Column("Method") { m ->
                    Span({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) { Text(m.matchMethod) }
                },
                Column("Created By") { m ->
                    Span({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) { Text(m.createdBy ?: "system") }
                },
                Column("Actions") { m ->
                    if (!isEditor) return@Column
                    Button({
                        onClick {
                            scope.launch {
                                try {
                                    apiClient.deleteEntityMapping(m.id)
                                    onRefresh()
                                } catch (_: Exception) {}
                            }
                        }
                        style {
                            padding(Spacing.xs, Spacing.sm)
                            fontSize(Typography.xs)
                            backgroundColor(Color("#ef444420"))
                            color(Color("#ef4444"))
                            border(0.px)
                            borderRadius(BorderRadius.sm)
                            cursor("pointer")
                        }
                    }) { Text("Unlink") }
                }
            )

            DataTable(
                columns = columns,
                rows = state.data
            )
        }
    }
}

@Composable
private fun UnmatchedTab(
    state: UiState<List<UnmatchedEntityResponse>>,
    isEditor: Boolean,
    onLink: (UnmatchedEntityResponse) -> Unit
) {
    val colors = AppTheme.colors

    when (state) {
        is UiState.Loading -> P({ style { color(Color(colors.textMuted)) } }) { Text("Loading...") }
        is UiState.Error -> P({ style { color(Color(colors.danger)) } }) { Text(state.message) }
        is UiState.Success -> {
            val columns = listOf(
                Column<UnmatchedEntityResponse>("Legacy ID") { e ->
                    Span({ style { fontFamily("monospace"); fontSize(Typography.sm) } }) { Text(e.legacyId) }
                },
                Column("Arabic Name") { e ->
                    Text(e.nameAr ?: "--")
                },
                Column("English Name") { e ->
                    Text(e.nameEn ?: "--")
                },
                Column("Actions") { e ->
                    if (!isEditor) return@Column
                    Button({
                        onClick { onLink(e) }
                        style {
                            padding(Spacing.xs, Spacing.sm)
                            primaryButtonStyle(colors)
                            fontSize(Typography.xs)
                            borderRadius(BorderRadius.sm)
                        }
                    }) { Text("Link") }
                }
            )

            DataTable(
                columns = columns,
                rows = state.data
            )
        }
    }
}

@Composable
private fun LinkEntityDialog(
    entity: UnmatchedEntityResponse,
    entityTypeFilter: String,
    apiClient: AdminApiClient,
    onLinked: () -> Unit,
    onClose: () -> Unit
) {
    val colors = AppTheme.colors
    var searchQuery by remember { mutableStateOf(entity.nameAr ?: entity.nameEn ?: "") }
    var suggestions by remember { mutableStateOf<List<AdminEntitySearchResult>>(emptyList()) }
    var selectedEntity by remember { mutableStateOf<AdminEntitySearchResult?>(null) }
    var isSearching by remember { mutableStateOf(false) }
    var isLinking by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    // Auto-search on query change
    LaunchedEffect(searchQuery) {
        if (searchQuery.length < 2) { suggestions = emptyList(); return@LaunchedEffect }
        isSearching = true
        try {
            suggestions = apiClient.searchEntities(searchQuery, entityTypeFilter, 10)
        } catch (_: Exception) {
            suggestions = emptyList()
        }
        isSearching = false
    }

    Modal(
        title = "Link ${entity.entityType}: ${entity.nameAr ?: entity.nameEn ?: entity.legacyId}",
        isOpen = true,
        onClose = onClose,
        maxWidth = 600.px
    ) {
        // Legacy entity info
        Div({
            style {
                padding(Spacing.md)
                marginBottom(Spacing.lg)
                borderRadius(BorderRadius.md)
                backgroundColor(Color(colors.cardBackground))
                property("border", "1px solid ${colors.border}")
            }
        }) {
            P({ style { fontSize(Typography.sm); color(Color(colors.textMuted)); marginBottom(Spacing.xs) } }) { Text("Legacy Entity") }
            P({ style { fontWeight("600") } }) { Text("${entity.nameAr ?: ""} ${if (entity.nameEn != null) "(${entity.nameEn})" else ""}") }
            P({ style { fontSize(Typography.sm); color(Color(colors.textMuted)); fontFamily("monospace") } }) { Text("Legacy ID: ${entity.legacyId}") }
        }

        // Search for catalog entity
        P({ style { fontSize(Typography.sm); fontWeight("600"); marginBottom(Spacing.xs) } }) { Text("Search Catalog Entity") }
        TextInput(searchQuery) {
            onInput { searchQuery = it.value; selectedEntity = null }
            placeholder("Search by name...")
            style {
                width(100.percent)
                padding(Spacing.md)
                borderRadius(BorderRadius.md)
                backgroundColor(Color(colors.cardBackground))
                color(Color(colors.text))
                fontSize(Typography.base)
                property("border", "1px solid ${colors.border}")
                property("box-sizing", "border-box")
            }
        }

        // Suggestions list
        if (suggestions.isNotEmpty() && selectedEntity == null) {
            Div({
                style {
                    marginTop(2.px)
                    borderRadius(BorderRadius.md)
                    property("border", "1px solid ${colors.border}")
                    backgroundColor(Color(colors.cardBackground))
                    property("max-height", "250px")
                    property("overflow-y", "auto")
                }
            }) {
                suggestions.forEach { result ->
                    Div({
                        onClick { selectedEntity = result; searchQuery = "${result.nameAr ?: result.nameEn}" }
                        style {
                            padding(Spacing.md)
                            cursor("pointer")
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(Spacing.md)
                            property("border-bottom", "1px solid ${colors.border}")
                            property("transition", "background-color 0.15s")
                        }
                    }) {
                        // Image
                        if (result.imageUrl != null) {
                            Img(src = result.imageUrl!!, alt = "") {
                                style { width(28.px); height(28.px); property("object-fit", "contain"); borderRadius(BorderRadius.sm) }
                            }
                        } else {
                            Div({ style { width(28.px); height(28.px); backgroundColor(Color(colors.border)); borderRadius(BorderRadius.sm) } })
                        }
                        // Name and ID
                        Div({ style { flex(1) } }) {
                            Div({ style { fontWeight("500") } }) { Text("${result.nameAr ?: ""} ${if (result.nameEn.isNotBlank()) "(${result.nameEn})" else ""}") }
                            Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)); fontFamily("monospace") } }) { Text(result.entityId) }
                        }
                    }
                }
            }
        }

        if (isSearching) {
            P({ style { fontSize(Typography.sm); color(Color(colors.textMuted)); marginTop(Spacing.sm) } }) { Text("Searching...") }
        }

        if (searchQuery.length >= 2 && suggestions.isEmpty() && !isSearching) {
            P({ style { fontSize(Typography.sm); color(Color(colors.textMuted)); marginTop(Spacing.sm) } }) { Text("No catalog entities found") }
        }

        // Selected entity preview
        selectedEntity?.let { selected ->
            Div({
                style {
                    marginTop(Spacing.md)
                    padding(Spacing.md)
                    borderRadius(BorderRadius.md)
                    backgroundColor(Color("#10b98110"))
                    property("border", "1px solid ${colors.success}")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(Spacing.md)
                }
            }) {
                if (selected.imageUrl != null) {
                    Img(src = selected.imageUrl!!, alt = "") {
                        style { width(32.px); height(32.px); property("object-fit", "contain"); borderRadius(BorderRadius.sm) }
                    }
                }
                Div({ style { flex(1) } }) {
                    P({ style { fontSize(Typography.sm); color(Color(colors.textMuted)); marginBottom(2.px) } }) { Text("Selected Catalog Entity") }
                    P({ style { fontWeight("600") } }) { Text("${selected.nameAr ?: ""} ${if (selected.nameEn.isNotBlank()) "(${selected.nameEn})" else ""}") }
                    P({ style { fontSize(Typography.xs); fontFamily("monospace"); color(Color(colors.textMuted)) } }) { Text(selected.entityId) }
                }
            }
        }

        error?.let { err ->
            P({ style { color(Color(colors.danger)); fontSize(Typography.sm); marginTop(Spacing.md) } }) { Text(err) }
        }

        // Buttons
        Div({ style { display(DisplayStyle.Flex); gap(Spacing.md); justifyContent(JustifyContent.FlexEnd); marginTop(Spacing.xl) } }) {
            Button({
                onClick { onClose() }
                style {
                    padding(Spacing.sm, Spacing.lg)
                    secondaryButtonStyle(colors)
                    fontSize(Typography.sm)
                    borderRadius(BorderRadius.md)
                }
            }) { Text("Cancel") }

            Button({
                onClick {
                    val catalogId = selectedEntity?.entityId
                    if (catalogId == null) {
                        error = "Select a catalog entity first"
                        return@onClick
                    }
                    scope.launch {
                        isLinking = true
                        error = null
                        try {
                            apiClient.createEntityMapping(CreateEntityMappingRequest(
                                entityType = entity.entityType,
                                legacyEntityId = entity.legacyId,
                                catalogEntityId = catalogId
                            ))
                            onLinked()
                        } catch (e: Exception) {
                            error = e.message ?: "Failed to create mapping"
                        }
                        isLinking = false
                    }
                }
                style {
                    padding(Spacing.sm, Spacing.lg)
                    primaryButtonStyle(colors)
                    fontSize(Typography.sm)
                    borderRadius(BorderRadius.md)
                    if (isLinking || selectedEntity == null) property("opacity", "0.6")
                }
            }) { Text(if (isLinking) "Linking..." else "Link") }
        }
    }
}
